import { Component, Input, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subject, takeUntil, forkJoin } from 'rxjs';

import { LocationOneAttributes, LocationTwoAttributes, LocationThreeAttributes } from '@albatrosdeveloper/ave-models-package';
import { getCode } from 'app/utils/method';
import { LocationOneService } from '../../locations/location-one/location-one.service.service';
import { LocationTwoService } from '../../locations/location-two/location-two.service';
import { LocationThreeService } from '../../locations/location-three/location-three.service';

@Component({
  selector: 'app-multi-location-filters',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatSnackBarModule,
    NgxMatSelectSearchModule
  ],
  templateUrl: './multi-location-filters.component.html',
  styleUrl: './multi-location-filters.component.scss'
})
export class MultiLocationFiltersComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() country: boolean = false;
  @Input() department: boolean = false;
  @Input() city: boolean = false;

  // Configuración de selección múltiple
  @Input() multiCountry: boolean = true;
  @Input() multiDepartment: boolean = true;
  @Input() multiCity: boolean = true;

  @Input() loadData: boolean = undefined;

  getCode = getCode;
  private destroy$ = new Subject<void>();

  // Estados de carga
  loadingCountries = false;
  loadingDepartments = false;
  loadingCities = false;

  // Listas de datos
  countryList: LocationOneAttributes[] = [];
  departmentList: LocationTwoAttributes[] = [];
  cityList: LocationThreeAttributes[] = [];

  // Listas filtradas para búsqueda
  filteredCountryList: LocationOneAttributes[] = [];
  filteredDepartmentList: LocationTwoAttributes[] = [];
  filteredCityList: LocationThreeAttributes[] = [];

  // Términos de búsqueda
  searchCountry: string = '';
  searchDepartment: string = '';
  searchCity: string = '';

  constructor(
    private locationOneService: LocationOneService,
    private locationTwoService: LocationTwoService,
    private locationThreeService: LocationThreeService,
    private snackBar: MatSnackBar
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loadData'] && changes['loadData'].currentValue === true) {
      this.loadAllData();
    }
  }

  ngOnInit(): void {
    if (this.loadData === undefined) {
      this.loadAllData();
    }
    this.setupFormSubscriptions();
  }

  private loadAllData(): void {
    if (this.country) {
      this.loadCountries();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Configura suscripciones a cambios del formulario
   */
  private setupFormSubscriptions(): void {
    // Suscribirse a cambios en país para cargar departamentos
    if (this.form.get('locationOneIds')) {
      this.form.get('locationOneIds')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(countryIds => {
          if (countryIds && countryIds.length > 0 && this.department) {
            this.loadDepartments(countryIds);
          } else {
            this.departmentList = [];
            this.filteredDepartmentList = [];
            this.form.get('locationTwoIds')?.setValue([]);
            this.form.get('locationThreeIds')?.setValue([]);
          }
        });
    }

    // Suscribirse a cambios en departamento para cargar ciudades
    if (this.form.get('locationTwoIds')) {
      this.form.get('locationTwoIds')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(departmentIds => {
          if (departmentIds && departmentIds.length > 0 && this.city) {
            this.loadCities(departmentIds);
          } else {
            this.cityList = [];
            this.filteredCityList = [];
            this.form.get('locationThreeIds')?.setValue([]);
          }
        });
    }
  }

  /**
   * Carga países
   */
  private loadCountries(): void {
    this.loadingCountries = true;
    this.locationOneService.findToComboBox().subscribe({
      next: (response) => {
        this.countryList = response.data || [];
        this.filteredCountryList = [...this.countryList];
        this.loadingCountries = false;
      },
      error: (error) => {
        const errorMessage = error?.error?.message || 'Error al cargar países';
        this.snackBar.open(errorMessage, 'Cerrar', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        this.loadingCountries = false;
      }
    });
  }

  /**
   * Carga departamentos basado en países seleccionados (múltiples)
   */
  private loadDepartments(countryIds: string[]): void {
    if (!countryIds.length) return;

    this.loadingDepartments = true;
    
    // Convertir IDs a códigos
    const selectedCountries = this.countryList.filter(country => countryIds.includes(country._id?.toString()));
    const countryCodes = selectedCountries.map(country => country.code).filter(Boolean);
    
    if (!countryCodes.length) {
      this.loadingDepartments = false;
      return;
    }
    
    // Si solo hay un país, usar la lógica simple
    if (countryCodes.length === 1) {
      this.locationTwoService.findToComboBox(countryCodes[0]).subscribe({
        next: (response) => {
          this.departmentList = response.data || [];
          this.filteredDepartmentList = [...this.departmentList];
          this.loadingDepartments = false;
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'Error al cargar departamentos';
          this.snackBar.open(errorMessage, 'Cerrar', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
          this.loadingDepartments = false;
        }
      });
    } else {
      // Para múltiples países, hacer llamadas paralelas y combinar resultados
      const requests = countryCodes.map(countryCode => 
        this.locationTwoService.findToComboBox(countryCode)
      );
      
      forkJoin(requests).subscribe({
        next: (responses) => {
          const allDepartments = responses.flatMap(r => r.data || []);
          // Remover duplicados basado en _id
          this.departmentList = allDepartments.filter((dept, index, self) => 
            index === self.findIndex(d => d._id === dept._id)
          );
          this.filteredDepartmentList = [...this.departmentList];
          this.loadingDepartments = false;
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'Error al cargar departamentos';
          this.snackBar.open(errorMessage, 'Cerrar', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
          this.loadingDepartments = false;
        }
      });
    }
  }

  /**
   * Carga ciudades basado en departamentos seleccionados (múltiples)
   */
  private loadCities(departmentIds: string[]): void {
    if (!departmentIds.length) return;

    this.loadingCities = true;
    
    // Convertir IDs a códigos
    const selectedDepartments = this.departmentList.filter(department => departmentIds.includes(department?._id?.toString()));
    const departmentCodes = selectedDepartments.map(department => department?.code).filter(Boolean);
    
    if (!departmentCodes.length) {
      this.loadingCities = false;
      return;
    }
    
    // Si solo hay un departamento, usar la lógica simple
    if (departmentCodes.length === 1) {
      this.locationThreeService.findToComboBox(departmentCodes[0]).subscribe({
        next: (response) => {
          this.cityList = response.data || [];
          this.filteredCityList = [...this.cityList];
          this.loadingCities = false;
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'Error al cargar ciudades';
          this.snackBar.open(errorMessage, 'Cerrar', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
          this.loadingCities = false;
        }
      });
    } else {
      // Para múltiples departamentos, hacer llamadas paralelas y combinar resultados
      const requests = departmentCodes.map(departmentCode => 
        this.locationThreeService.findToComboBox(departmentCode)
      );
      
      forkJoin(requests).subscribe({
        next: (responses) => {
          const allCities = responses.flatMap(r => r.data || []);
          // Remover duplicados basado en _id
          this.cityList = allCities.filter((city, index, self) => 
            index === self.findIndex(c => c._id === city._id)
          );
          this.filteredCityList = [...this.cityList];
          this.loadingCities = false;
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'Error al cargar ciudades';
          this.snackBar.open(errorMessage, 'Cerrar', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
          this.loadingCities = false;
        }
      });
    }
  }

  /**
   * Filtra países por término de búsqueda
   */
  filterCountry(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredCountryList = [...this.countryList];
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    this.filteredCountryList = this.countryList.filter(country =>
      country.name.toLowerCase().includes(searchTermLower) ||
      country.code?.toLowerCase().includes(searchTermLower)
    );
  }

  /**
   * Filtra departamentos por término de búsqueda
   */
  filterDepartment(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredDepartmentList = [...this.departmentList];
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    this.filteredDepartmentList = this.departmentList.filter(department =>
      department.name.toLowerCase().includes(searchTermLower) ||
      department.code?.toLowerCase().includes(searchTermLower)
    );
  }

  /**
   * Filtra ciudades por término de búsqueda
   */
  filterCity(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredCityList = [...this.cityList];
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    this.filteredCityList = this.cityList.filter(city =>
      city.name.toLowerCase().includes(searchTermLower) ||
      city.code?.toLowerCase().includes(searchTermLower)
    );
  }

  /**
   * Obtiene valor para mostrar en el trigger del select
   */
  getDisplayValue(list: { _id: string; name: string }[], selectedValues: (string | number)[], propertyName: string = 'name'): string {
    if (!selectedValues || !selectedValues.length) return '';
    
    if (selectedValues.length === 1) {
      const item = list.find(item => item._id === selectedValues[0]);
      return item ? item[propertyName] : '';
    }
    
    return `${selectedValues.length} seleccionados`;
  }
}